import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { getColorScheme } from '../theme/colorSchemes';
import { isValidNocCode, lookupNocCode, searchNocByTitle, NOC_LOOKUP_URL } from '../assessments/oinp/noc';
import { highWageTier } from '../assessments/crs/labourMarket';

// Occupation picker for the CRS Labour Market Integration factor. Type a job
// title (searched server-side in the official NOC 2021 index) or a 5-digit code.
// A code is only accepted once the official index confirms it exists, so a
// made-up code can never earn high-wage points.

const CrsNocPicker = ({ label, helpText, value, title, onChange, tr, darkMode, scheme }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const abortRef = useRef(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2 || isValidNocCode(q)) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const found = await searchNocByTitle(q, { signal: controller.signal });
      if (!controller.signal.aborted) {
        setResults(found);
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  const choose = async (code, knownTitle) => {
    setError('');
    const official = await lookupNocCode(code);
    if (!official) {
      setError(tr('lmiNocNotFound', { code }));
      return;
    }
    setQuery('');
    setResults([]);
    onChange(code, knownTitle || official.title || '');
  };

  const tier = highWageTier(value);

  return (
    <Field>
      <Label $darkMode={darkMode}>{label}</Label>
      {helpText && <Help $darkMode={darkMode}>{helpText}</Help>}
      {value ? (
        <Chosen $darkMode={darkMode} $scheme={scheme}>
          <div>
            <strong>NOC {value}</strong>{title ? ` — ${title}` : ''}
            <Tier $high={Boolean(tier)}>
              {tier ? tr('lmiTier', { tier }) : tr('lmiNotHighWage')}
            </Tier>
          </div>
          <Clear type="button" $darkMode={darkMode} onClick={() => onChange('', '')}>{tr('lmiChange')}</Clear>
        </Chosen>
      ) : (
        <>
          <Input
            $darkMode={darkMode}
            $scheme={scheme}
            value={query}
            placeholder={tr('lmiNocPlaceholder')}
            onChange={e => { setQuery(e.target.value); setError(''); }}
            onKeyDown={e => { if (e.key === 'Enter' && isValidNocCode(query.trim())) choose(query.trim()); }}
          />
          {isValidNocCode(query.trim()) && (
            <Result $darkMode={darkMode} onClick={() => choose(query.trim())}>
              {tr('lmiUseCode', { code: query.trim() })}
            </Result>
          )}
          {results.map(r => (
            <Result key={r.code} $darkMode={darkMode} onClick={() => choose(r.code, r.title)}>
              <strong>{r.code}</strong> {r.title}
              {r.matchedOn && r.matchedOn.toLowerCase() !== String(r.title).toLowerCase() && <Muted> · {r.matchedOn}</Muted>}
            </Result>
          ))}
          {searching && <Help $darkMode={darkMode}>{tr('lmiSearching')}</Help>}
          {!searching && query.trim().length >= 2 && !isValidNocCode(query.trim()) && results.length === 0 && (
            <Help $darkMode={darkMode}>{tr('lmiNoResults')}</Help>
          )}
          <Link $darkMode={darkMode} href={NOC_LOOKUP_URL} target="_blank" rel="noopener noreferrer">{tr('lmiNocLookup')}</Link>
        </>
      )}
      {error && <Err>{error}</Err>}
    </Field>
  );
};

const Field = styled.div`
  margin-bottom: 14px;
  width: 100%;
  box-sizing: border-box;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  font-size: 12px;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.9)' : '#1a2332'};
`;

const Help = styled.p`
  margin: 0 0 5px 0;
  font-size: 11px;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.5)' : '#6b7a8d'};
  line-height: 1.4;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 2px solid ${p => getColorScheme(p.$scheme).borderAccent};
  font-size: 12px;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  background: ${p => p.$darkMode ? getColorScheme(p.$scheme).bgInput : '#fff'};
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.9)' : '#1a2332'};
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: ${p => getColorScheme(p.$scheme).primary}; }
`;

const Result = styled.div`
  padding: 8px 12px;
  margin-top: 4px;
  border-radius: 8px;
  font-size: 12px;
  cursor: pointer;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.9)' : '#1a2332'};
  background: ${p => p.$darkMode ? '#2a2a2a' : '#f5f8fd'};
  &:hover { background: ${p => p.$darkMode ? '#333' : '#e9effa'}; }
`;

const Muted = styled.span`
  opacity: 0.6;
`;

const Chosen = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 2px solid ${p => getColorScheme(p.$scheme).primary};
  font-size: 12px;
  line-height: 1.4;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.9)' : '#1a2332'};
  background: ${p => p.$darkMode ? '#1e1e1e' : '#fff'};
`;

const Tier = styled.div`
  margin-top: 4px;
  font-weight: 600;
  color: ${p => p.$high ? '#15803d' : '#64748b'};
`;

const Clear = styled.button`
  flex-shrink: 0;
  border: none;
  background: none;
  padding: 0;
  font-size: 11px;
  text-decoration: underline;
  cursor: pointer;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.7)' : '#3359c7'};
`;

const Link = styled.a`
  display: inline-block;
  margin-top: 6px;
  font-size: 11px;
  color: ${p => p.$darkMode ? 'rgba(255,255,255,0.7)' : '#3359c7'};
`;

const Err = styled.p`
  margin: 6px 0 0;
  font-size: 11px;
  color: #dc2626;
`;

export default CrsNocPicker;
